import { computed, inject, Injectable, signal } from '@angular/core';
import { AuthService } from '../oauth/auth.service';

export interface Tenant {
  name: string;
  apiUrl: string;
  stsUrl: string;
  webClientUrl?: string;
}

@Injectable({
  providedIn: 'root'
})
export class TenantService {
  private readonly _authService = inject(AuthService);
  private readonly _storageKey = 'tenants';
  private readonly _selectedTenantNameKey = 'selectedTenantName';
  public readonly tenants = signal<Tenant[]>([]);
  public readonly selectedTenantName = signal<string | null>(null);
  public readonly selectedTenant = computed(() => {
    const name = this.selectedTenantName();
    return name ? this.tenants().find((t) => t.name === name) : undefined;
  });

  constructor() {
    this.loadTenants();
    this._loadSelectedTenantName();
  }

  public loadTenants(): void {
    const stored = localStorage.getItem(this._storageKey);
    if (stored) {
      this.tenants.set(JSON.parse(stored) as Tenant[]);
    }
  }

  private _loadSelectedTenantName(): void {
    const stored = localStorage.getItem(this._selectedTenantNameKey);
    if (stored) {
      this.selectedTenantName.set(stored);
    }
  }

  public selectTenant(name: string): void {
    const previousTenantName = this.selectedTenantName();
    if (previousTenantName !== name) {
      void this._authService.clearAuth();
    }
    this.selectedTenantName.set(name);
    localStorage.setItem(this._selectedTenantNameKey, name);
  }

  public saveTenant(tenant: Tenant): void {
    const updated = [...this.tenants(), tenant];
    this.tenants.set(updated);
    localStorage.setItem(this._storageKey, JSON.stringify(updated));

    // Auto-select the first tenant if no tenant is selected
    if (updated.length === 1) {
      this.selectTenant(tenant.name);
    }
  }

  public getTenants(): Tenant[] {
    return this.tenants();
  }

  public deleteTenant(name: string): void {
    const updated = this.tenants().filter((tenant) => tenant.name !== name);
    this.tenants.set(updated);
    localStorage.setItem(this._storageKey, JSON.stringify(updated));

    // If the selected tenant was deleted, auto-select the first remaining tenant
    if (this.selectedTenantName() === name) {
      if (updated.length > 0) {
        this.selectTenant(updated[0].name);
      } else {
        void this._authService.clearAuth();
        this.selectedTenantName.set(null);
        localStorage.removeItem(this._selectedTenantNameKey);
      }
    } else if (updated.length === 1) {
      // Auto-select the remaining tenant if only one remains
      this.selectTenant(updated[0].name);
    }
  }

  public updateTenant(oldName: string, tenant: Tenant): void {
    const updated = this.tenants().map((t) => (t.name === oldName ? tenant : t));
    this.tenants.set(updated);
    localStorage.setItem(this._storageKey, JSON.stringify(updated));

    // Update selected tenant name if it was the one being edited
    if (this.selectedTenantName() === oldName) {
      this.selectedTenantName.set(tenant.name);
      localStorage.setItem(this._selectedTenantNameKey, tenant.name);
    }
  }

  public getTenantByName(name: string): Tenant | undefined {
    return this.tenants().find((tenant) => tenant.name === name);
  }
}
